import { AxisRight } from '@visx/axis';
import { scaleBand, scaleLinear } from '@visx/scale';
import { ParentSize } from '@visx/responsive';
import clsx from 'clsx';
import { Heading, Text } from '@mysten/ui';
import { useMemo, useEffect, useState } from 'react';

import type { KlineCandle } from '~/hooks/usePushHub';
import { Card } from '~/ui/Card';

import { TRADE, formatTimeMs, KLINE_CHART_HEIGHT_PX, KLINE_INTERVALS, type KlineInterval } from './tradeTheme';

const UP = TRADE.up;
const DOWN = TRADE.down;

function sma(values: number[], period: number): (number | null)[] {
    return values.map((_, i) => {
        if (i + 1 < period) return null;
        let s = 0;
        for (let j = i - period + 1; j <= i; j++) s += values[j]!;
        return s / period;
    });
}

// 辅助函数：将收盘价点阵生成平滑的三次贝塞尔曲线路径
function getBezierPath(points: { x: number; y: number }[]): string {
    if (points.length === 0) return '';
    if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

    let d = `M ${points[0].x} ${points[0].y}`;
    for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[i];
        const p1 = points[i + 1];
        const cpX1 = p0.x + (p1.x - p0.x) / 2;
        const cpY1 = p0.y;
        const cpX2 = p0.x + (p1.x - p0.x) / 2;
        const cpY2 = p1.y;
        d += ` C ${cpX1} ${cpY1}, ${cpX2} ${cpY2}, ${p1.x} ${p1.y}`;
    }
    return d;
}

function KlineSvg({
    candles,
    width,
    height,
    hubLive,
}: {
    candles: KlineCandle[];
    width: number;
    height: number;
    hubLive?: boolean;
}) {
    // 检测是否为 H5 移动端
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        setIsMobile(window.innerWidth < 768);
    }, []);

    const rightMargin = isMobile ? 44 : 64;
    const margin = { top: 16, right: rightMargin, bottom: 24, left: 8 };
    const innerW = Math.max(0, width - margin.left - margin.right);
    const priceH = Math.floor(height * 0.68); // 略微压低价格图占比，防 H5 下太拥挤
    const volH = Math.max(0, height - margin.top - margin.bottom - priceH - 6);
    const innerPriceH = Math.max(0, priceH - margin.top);
    const volTop = margin.top + innerPriceH + 6;

    const { xScale, yScale, volScale, ma7, lastClose, linePoints } = useMemo(() => {
        if (candles.length === 0) {
            return {
                xScale: scaleBand<number>({ domain: [], range: [0, innerW], padding: 0.2 }),
                yScale: scaleLinear<number>({ domain: [0, 1], range: [innerPriceH, 0] }),
                volScale: scaleLinear<number>({ domain: [0, 1], range: [volH, 0] }),
                ma7: [] as (number | null)[],
                lastClose: 0,
                linePoints: [] as { x: number; y: number }[],
            };
        }
        const lows = candles.map((c) => c.low);
        const highs = candles.map((c) => c.high);
        const min = Math.min(...lows);
        const max = Math.max(...highs);
        const pad = (max - min) * 0.1 || max * 0.01 || 0.001;
        const closesArr = candles.map((c) => c.close);
        const volMax = Math.max(...candles.map((c) => Number(c.volumeLots || '0')), 1);

        const xS = scaleBand<number>({
            domain: candles.map((c) => c.openTimeMs),
            range: [0, innerW],
            padding: 0.2,
        });

        const yS = scaleLinear<number>({
            domain: [min - pad, max + pad],
            range: [innerPriceH, 0],
            nice: true,
        });

        const band = xS.bandwidth();
        const points = candles.map((c) => {
            const x = xS(c.openTimeMs) ?? 0;
            return {
                x: x + band / 2,
                y: yS(c.close),
            };
        });

        return {
            xScale: xS,
            yScale: yS,
            volScale: scaleLinear<number>({
                domain: [0, volMax],
                range: [volH, 0],
            }),
            ma7: sma(closesArr, 7),
            lastClose: closesArr[closesArr.length - 1] ?? 0,
            linePoints: points,
        };
    }, [candles, innerW, innerPriceH, volH]);

    if (candles.length === 0) {
        return (
            <div className="flex h-full min-h-[160px] items-center justify-center rounded-xl bg-white/10 px-4 text-center">
                <Text variant="body/medium" color="dark">
                    {hubLive === false
                        ? 'Push Hub 未连线 — 盘口/K 线暂不可用'
                        : '暂无 K 线 — 该交易对尚无成交（Hub 已连线）'}
                </Text>
            </div>
        );
    }

    const band = xScale.bandwidth();
    const xTicks = isMobile
        ? [0, candles.length - 1].filter((i) => i >= 0 && i < candles.length)
        : [0, Math.floor(candles.length / 2), candles.length - 1].filter((i) => i >= 0 && i < candles.length);

    const mainLinePath = getBezierPath(linePoints);
    let areaPath = '';
    if (linePoints.length > 0) {
        const firstPoint = linePoints[0];
        const lastPoint = linePoints[linePoints.length - 1];
        areaPath = `${mainLinePath} L ${lastPoint.x} ${innerPriceH} L ${firstPoint.x} ${innerPriceH} Z`;
    }

    return (
        <svg width={width} height={height} className="select-none overflow-visible">
            <defs>
                <filter id="cpc-glow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="6" result="blur" />
                    <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
                <linearGradient id="cpc-area-gradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#38BDF8" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#38BDF8" stopOpacity={0.0} />
                </linearGradient>
            </defs>

            <g transform={`translate(${margin.left},${margin.top})`}>
                {yScale.ticks(4).map((tick) => (
                    <line
                        key={`pg-${tick}`}
                        x1={0}
                        x2={innerW}
                        y1={yScale(tick)}
                        y2={yScale(tick)}
                        stroke={TRADE.grid}
                        strokeDasharray="2,4"
                    />
                ))}

                {areaPath && <path d={areaPath} fill="url(#cpc-area-gradient)" />}

                {mainLinePath && (
                    <path
                        d={mainLinePath}
                        fill="none"
                        stroke="#38BDF8"
                        strokeWidth={2.5}
                        filter="url(#cpc-glow)"
                        strokeLinecap="round"
                    />
                )}

                <path
                    fill="none"
                    stroke={TRADE.accent}
                    strokeWidth={1.2}
                    opacity={0.5}
                    d={ma7
                        .map((v, i) => {
                            if (v == null) return '';
                            const x = (xScale(candles[i]!.openTimeMs) ?? 0) + band / 2;
                            const y = yScale(v);
                            return `${i === 0 || ma7[i - 1] == null ? 'M' : 'L'} ${x} ${y}`;
                        })
                        .join(' ')}
                />

                <line
                    x1={0}
                    x2={innerW}
                    y1={yScale(lastClose)}
                    y2={yScale(lastClose)}
                    stroke={TRADE.accent}
                    strokeDasharray="4,3"
                    opacity={0.6}
                />

                <AxisRight
                    left={innerW}
                    scale={yScale}
                    numTicks={4}
                    stroke="transparent"
                    tickStroke="transparent"
                    tickFormat={(v) => String(Number(v))}
                    tickLabelProps={() => ({ 
                        fill: TRADE.muted, 
                        fontSize: isMobile ? 9 : 10, 
                        textAnchor: 'start', 
                        dx: isMobile ? 3 : 6, 
                        dy: 3 
                    })}
                />
            </g>

            <g transform={`translate(${margin.left},${volTop})`}>
                {candles.map((c) => {
                    const x = xScale(c.openTimeMs) ?? 0;
                    const vol = Number(c.volumeLots || '0');
                    const up = c.close >= c.open;
                    const h = volH - volScale(vol);
                    return (
                        <rect
                            key={`v-${c.openTimeMs}`}
                            x={x + 1}
                            y={volScale(vol)}
                            width={Math.max(1, band - 2)}
                            height={Math.max(0, h)}
                            fill={up ? UP : DOWN}
                            opacity={0.6}
                        />
                    );
                })}
                {xTicks.map((i) => {
                    const c = candles[i]!;
                    const x = (xScale(c.openTimeMs) ?? 0) + band / 2;
                    return (
                        <text key={c.openTimeMs} x={x + margin.left} y={height - 4} fill={TRADE.muted} fontSize={9} textAnchor="middle">
                            {formatTimeMs(c.openTimeMs)}
                        </text>
                    );
                })}
            </g>
        </svg>
    );
}

export function CpcKlineChart({
    candles,
    height = KLINE_CHART_HEIGHT_PX,
    interval,
    onIntervalChange,
    hubLive,
}: {
    candles: KlineCandle[];
    height?: number;
    interval: KlineInterval;
    onIntervalChange: (iv: KlineInterval) => void;
    /** Push Hub WebSocket connected */
    hubLive?: boolean;
}) {
    // 1. 通过 JS 动态实时获取当前屏幕视口宽度，杜绝 className 无法透传给底层组件的问题
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        setIsMobile(window.innerWidth < 768);
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        /* 2. 移除 growOnHover 传值，避免其内部逻辑干扰移动端。
           如果项目内底层 Card 高度被强制撑大，我们现在通过下面的子内部层来强行控制。
        */
        <Card bg="dark" spacing="sm">
            {/* 3. 【最核心解决点】直接在最内层的第一层 div 上进行高度绝对控制！
               - PC 端 (isMobile 为 false): 高度继续保持百分之百自适应填满大容器（h-full）
               - H5 端 (isMobile 为 true): 强制定死在 260px (可以根据你的感觉改写，比如 280px / 300px)
               用内联 style 强制截断，无论底层组件怎么写，高度在 H5 端必定能完美缩小！
            */}
            <div 
                className="flex min-h-0 flex-col gap-2 w-full justify-between"
                style={isMobile ? { height: '260px' } : { height: '100%' }}
            >
                {/* 顶部栏：防横向换行错位 */}
                <div className="flex flex-row items-center justify-between gap-2 shrink-0">
                    <Heading variant="heading4/semibold" color="dark" className="text-[15px] md:text-[16px] truncate">
                        Price Chart
                    </Heading>
                    <div className="flex items-center gap-1 md:gap-2 overflow-x-auto no-scrollbar">
                        {KLINE_INTERVALS.map((iv) => (
                            <button
                                key={iv}
                                type="button"
                                className={clsx(
                                    'rounded-md px-2 md:px-3 py-0.5 md:py-1 text-[11px] md:text-caption font-semibold transition shrink-0',
                                    interval === iv
                                        ? 'bg-[#38BDF8] text-[#ffffff]'
                                        : 'text-[#ffffff]',
                                )}
                                onClick={() => onIntervalChange(iv)}
                            >
                                {iv}
                            </button>
                        ))}
                    </div>
                </div>

                {/* 图表真实区域：flex-1 自动算高分配空间 */}
                <div className="w-full flex-1 min-h-0">
                    <ParentSize debounceTime={50}>
                        {({ width, height: h }) => (
                            <KlineSvg candles={candles} width={width} height={h} hubLive={hubLive} />
                        )}
                    </ParentSize>
                </div>

                {/* 底部文案区 */}
                <div className="flex items-center justify-between pt-0.5 text-[10px] md:text-caption opacity-70 shrink-0 gap-1">
                    <Text variant="caption/medium" color="dark">
                        Real-time data stream
                    </Text>
                    <Text variant="caption/medium" color="dark">
                        Vol · {candles.length} bars
                    </Text>
                </div>

            </div>
        </Card>
    );
}